import React from 'react';
import { ATSResumeJson } from '@/types/resume';
import { Plus, Trash2 } from 'lucide-react';
import { RichInput } from '@/components/ai-tools/RichInput';
import { GlobalFormatToolbar } from '@/components/ai-tools/GlobalFormatToolbar';

interface Props {
  data: ATSResumeJson;
  onChange: (data: ATSResumeJson) => void;
}

export function Editor({ data, onChange }: Props) {
  const updateNested = (category: keyof ATSResumeJson, index: number, field: string, value: any) => {
    const list = [...(data[category] as any[] || [])];
    list[index] = { ...list[index], [field]: value };
    onChange({ ...data, [category]: list });
  };

  const addNested = (category: Exclude<keyof ATSResumeJson, 'basics' | 'summary'>, emptyItem: any) => {
    const list = [...(data[category] as any[] || [])];
    list.push(emptyItem);
    onChange({ ...data, [category]: list });
  };

  const removeNested = (category: Exclude<keyof ATSResumeJson, 'basics' | 'summary'>, index: number) => {
    const list = [...(data[category] as any[] || [])];
    list.splice(index, 1);
    onChange({ ...data, [category]: list });
  };

  const updateArrayEntryRoot = (category: 'honors' | 'languages', index: number, value: string) => {
    const list = [...(data[category] || [])];
    list[index] = value;
    onChange({ ...data, [category]: list });
  };

  const addArrayEntryRoot = (category: 'honors' | 'languages') => {
    const list = [...(data[category] || [])];
    list.push("");
    onChange({ ...data, [category]: list });
  };

  const removeArrayEntryRoot = (category: 'honors' | 'languages', index: number) => {
    const list = [...(data[category] || [])];
    list.splice(index, 1);
    onChange({ ...data, [category]: list });
  };

  const updateArrayEntry = (category: keyof ATSResumeJson, index: number, arrayField: string, arrayIndex: number, value: string) => {
    const list = [...(data[category] as any[] || [])];
    const item = { ...list[index] };
    const arr = [...(item[arrayField] || [])];
    arr[arrayIndex] = value;
    item[arrayField] = arr;
    list[index] = item;
    onChange({ ...data, [category]: list });
  };

  const addArrayEntry = (category: keyof ATSResumeJson, index: number, arrayField: string) => {
    const list = [...(data[category] as any[] || [])];
    const item = { ...list[index] };
    const arr = [...(item[arrayField] || [])];
    arr.push("");
    item[arrayField] = arr;
    list[index] = item;
    onChange({ ...data, [category]: list });
  };

  const removeArrayEntry = (category: keyof ATSResumeJson, index: number, arrayField: string, arrayIndex: number) => {
    const list = [...(data[category] as any[] || [])];
    const item = { ...list[index] };
    const arr = [...(item[arrayField] || [])];
    arr.splice(arrayIndex, 1);
    item[arrayField] = arr;
    list[index] = item;
    onChange({ ...data, [category]: list });
  };

  const updateBasicsUrl = (index: number, field: string, value: string) => {
    const urls = [...(data.basics?.urls || [])];
    urls[index] = { ...urls[index], [field]: value };
    onChange({ ...data, basics: { ...data.basics!, urls } });
  };

  const addBasicsUrl = () => {
    const urls = [...(data.basics?.urls || [])];
    urls.push({ label: '', url: '' });
    onChange({ ...data, basics: { ...data.basics!, urls } });
  };

  const removeBasicsUrl = (index: number) => {
    const urls = [...(data.basics?.urls || [])];
    urls.splice(index, 1);
    onChange({ ...data, basics: { ...data.basics!, urls } });
  };

  return (
    <div className="max-w-4xl mx-auto w-full space-y-8 p-4 sm:p-8 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 relative">
       <GlobalFormatToolbar />
       
       {/* Basics section */}
       <section className="space-y-4">
         <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 border-b border-slate-200 dark:border-slate-700 pb-2">Personal Information</h2>
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
           <div>
             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
             <input className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white" value={data.basics?.name || ''} onChange={(e) => onChange({...data, basics: {...data.basics!, name: e.target.value}})} />
           </div>
           <div>
             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
             <input className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white" value={data.basics?.title || ''} onChange={(e) => onChange({...data, basics: {...data.basics!, title: e.target.value}})} />
           </div>
           <div>
             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
             <input className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white" value={data.basics?.email || ''} onChange={(e) => onChange({...data, basics: {...data.basics!, email: e.target.value}})} />
           </div>
           <div>
             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
             <input className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white" value={data.basics?.phone || ''} onChange={(e) => onChange({...data, basics: {...data.basics!, phone: e.target.value}})} />
           </div>
           <div className="sm:col-span-2">
             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Location</label>
             <input className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white" value={data.basics?.location || ''} onChange={(e) => onChange({...data, basics: {...data.basics!, location: e.target.value}})} />
           </div>
         </div>
         
         <div className="pt-2">
           <div className="flex items-center justify-between mb-2">
             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Links (LinkedIn, GitHub, Portfolio)</label>
             <button onClick={addBasicsUrl} className="text-xs flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
               <Plus className="w-3 h-3" /> Add Link
             </button>
           </div>
           <div className="space-y-2">
             {data.basics?.urls?.map((urlItem, uIdx) => (
               <div key={uIdx} className="flex items-center gap-2">
                 <input className="w-1/3 p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white text-sm" placeholder="Label (e.g. GitHub)" value={urlItem.label || ''} onChange={(e) => updateBasicsUrl(uIdx, 'label', e.target.value)} />
                 <input className="flex-1 p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white text-sm" placeholder="URL" value={urlItem.url || ''} onChange={(e) => updateBasicsUrl(uIdx, 'url', e.target.value)} />
                 <button onClick={() => removeBasicsUrl(uIdx)} className="p-2 text-slate-400 hover:text-red-500" title="Remove Link">
                   <Trash2 className="w-4 h-4" />
                 </button>
               </div>
             ))}
             {(!data.basics?.urls || data.basics.urls.length === 0) && (
               <p className="text-xs text-slate-500 italic">No links added.</p>
             )}
           </div>
         </div>

         <div>
           <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Professional Summary</label>
           <RichInput 
             value={data.summary || ''} 
             onChange={(v) => onChange({...data, summary: v})} 
             minHeight="120px" 
             placeholder="Senior professional with 7+ years of experience..."
           />
         </div>
       </section>
       
       {/* Experience */}
       <section className="space-y-4">
         <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
           <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Work Experience</h2>
           <button onClick={() => addNested('experience', { company: '', title: '', location: '', startDate: '', endDate: '', highlights: [''] })} className="text-sm flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
             <Plus className="w-4 h-4" /> Add Job
           </button>
         </div>
         {data.experience?.map((job, idx) => (
           <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg space-y-4 bg-white dark:bg-slate-950 relative group dark:shadow-sm transition-dark:shadow dark:hover:shadow">
             <button onClick={() => removeNested('experience', idx)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove Job">
               <Trash2 className="w-4 h-4" />
             </button>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pr-6">
               <div>
                 <label className="block text-xs font-medium text-slate-500 mb-1">Company</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={job.company || ''} onChange={(e) => updateNested('experience', idx, 'company', e.target.value)} />
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-500 mb-1">Title</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={job.title || ''} onChange={(e) => updateNested('experience', idx, 'title', e.target.value)} />
               </div>
               <div className="sm:col-span-2">
                 <label className="block text-xs font-medium text-slate-500 mb-1">Location</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={job.location || ''} onChange={(e) => updateNested('experience', idx, 'location', e.target.value)} />
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-500 mb-1">Start Date</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={job.startDate || ''} onChange={(e) => updateNested('experience', idx, 'startDate', e.target.value)} />
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-500 mb-1">End Date</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={job.endDate || ''} onChange={(e) => updateNested('experience', idx, 'endDate', e.target.value)} />
               </div>
             </div>
             
             <div>
               <label className="block text-xs font-medium text-slate-500 mb-2">Highlights / Responsibilities</label>
               <div className="space-y-2">
                 {job.highlights?.map((hl, hlIdx) => (
                   <div key={hlIdx} className="flex items-start gap-2">
                     <span className="mt-3 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
                     <div className="flex-1 min-w-0">
                       <RichInput 
                         value={hl} 
                         onChange={(v) => updateArrayEntry('experience', idx, 'highlights', hlIdx, v)} 
                         minHeight="60px"
                       />
                     </div>
                     <button onClick={() => removeArrayEntry('experience', idx, 'highlights', hlIdx)} className="p-2 mt-1 text-slate-400 hover:text-red-500" title="Remove Highlight">
                       <Trash2 className="w-4 h-4" />
                     </button>
                   </div>
                 ))}
                 <button onClick={() => addArrayEntry('experience', idx, 'highlights')} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
                   + Add bullet point
                 </button>
               </div>
             </div>
           </div>
         ))}
       </section>

       {/* Education */}
       <section className="space-y-4">
         <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
           <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Education</h2>
           <button onClick={() => addNested('education', { institution: '', degree: '', startDate: '', endDate: '' })} className="text-sm flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
             <Plus className="w-4 h-4" /> Add Education
           </button>
         </div>
         {data.education?.map((edu, idx) => (
           <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg space-y-4 bg-white dark:bg-slate-950 relative group dark:shadow-sm transition-dark:shadow dark:hover:shadow">
             <button onClick={() => removeNested('education', idx)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove Education">
               <Trash2 className="w-4 h-4" />
             </button>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pr-6">
               <div className="sm:col-span-2">
                 <label className="block text-xs font-medium text-slate-500 mb-1">Institution</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={edu.institution || ''} onChange={(e) => updateNested('education', idx, 'institution', e.target.value)} />
               </div>
               <div className="sm:col-span-2">
                 <label className="block text-xs font-medium text-slate-500 mb-1">Degree</label>
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={edu.degree || ''} onChange={(e) => updateNested('education', idx, 'degree', e.target.value)} />
               </div>
               <div className="grid grid-cols-2 gap-2 sm:col-span-2">
                 <div>
                   <label className="block text-xs font-medium text-slate-500 mb-1">Start Date</label>
                   <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={edu.startDate || ''} onChange={(e) => updateNested('education', idx, 'startDate', e.target.value)} />
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-slate-500 mb-1">End Date</label>
                   <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={edu.endDate || ''} onChange={(e) => updateNested('education', idx, 'endDate', e.target.value)} />
                 </div>
               </div>
             </div>
           </div>
         ))}
       </section>

       {/* Projects */}
       <section className="space-y-4">
         <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
           <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Projects</h2>
           <button onClick={() => addNested('projects', { name: '', description: '', url: '', highlights: [''] })} className="text-sm flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
             <Plus className="w-4 h-4" /> Add Project
           </button>
         </div>
         {data.projects?.map((proj, idx) => (
           <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg space-y-4 bg-white dark:bg-slate-950 relative group dark:shadow-sm transition-dark:shadow dark:hover:shadow">
             <button onClick={() => removeNested('projects', idx)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove Project">
               <Trash2 className="w-4 h-4" />
             </button>
             <div className="pr-6">
               <label className="block text-xs font-medium text-slate-500 mb-1">Project Name</label>
               <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white mb-2" value={proj.name || ''} onChange={(e) => updateNested('projects', idx, 'name', e.target.value)} />
               
               <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
               <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white mb-2" value={proj.description || ''} onChange={(e) => updateNested('projects', idx, 'description', e.target.value)} />

               <label className="block text-xs font-medium text-slate-500 mb-1">URL (Optional)</label>
               <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white mb-2" value={proj.url || ''} onChange={(e) => updateNested('projects', idx, 'url', e.target.value)} />
             </div>
             
             <div>
               <label className="block text-xs font-medium text-slate-500 mb-2">Highlights</label>
               <div className="space-y-2">
                 {proj.highlights?.map((hl, hlIdx) => (
                   <div key={hlIdx} className="flex items-start gap-2">
                     <span className="mt-3 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
                     <div className="flex-1 min-w-0">
                       <RichInput 
                         value={hl} 
                         onChange={(v) => updateArrayEntry('projects', idx, 'highlights', hlIdx, v)} 
                         minHeight="60px"
                       />
                     </div>
                     <button onClick={() => removeArrayEntry('projects', idx, 'highlights', hlIdx)} className="p-2 mt-1 text-slate-400 hover:text-red-500" title="Remove Highlight">
                       <Trash2 className="w-4 h-4" />
                     </button>
                   </div>
                 ))}
                 <button onClick={() => addArrayEntry('projects', idx, 'highlights')} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
                   + Add bullet point
                 </button>
               </div>
             </div>
           </div>
         ))}
       </section>

       {/* Skills */}
       <section className="space-y-4">
         <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
           <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Skills</h2>
           <button onClick={() => addNested('skills', { category: '', items: [] })} className="text-sm flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
             <Plus className="w-4 h-4" /> Add Skill Category
           </button>
         </div>
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
           {data.skills?.map((skill, idx) => (
             <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg space-y-3 bg-white dark:bg-slate-950 relative group dark:shadow-sm transition-dark:shadow dark:hover:shadow">
               <button onClick={() => removeNested('skills', idx)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove Skill">
                 <Trash2 className="w-4 h-4" />
               </button>
               <div className="pr-6">
                 <label className="block text-xs font-medium text-slate-500 mb-1">Category (e.g., Languages, Tools)</label>
                 <RichInput value={skill.category || ''} onChange={(val) => updateNested('skills', idx, 'category', val)} minHeight="38px" />
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-500 mb-1">Skills (Comma separated)</label>
                 <RichInput 
                   value={(skill.items || []).join(', ')} 
                   onChange={(val) => {
                     const kws = val.split(',').map(s => s.trim()).filter(s => s);
                     updateNested('skills', idx, 'items', kws);
                   }} 
                   minHeight="38px" 
                 />
               </div>
             </div>
           ))}
         </div>
       </section>

       {/* Languages */}
       <section className="space-y-4">
         <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
           <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Languages</h2>
           <button onClick={() => addArrayEntryRoot('languages')} className="text-sm flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
             <Plus className="w-4 h-4" /> Add Language
           </button>
         </div>
         <div className="space-y-2">
           {data.languages?.map((lang, idx) => (
             <div key={idx} className="flex items-start gap-2">
               <span className="mt-3 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
               <div className="flex-1 min-w-0">
                 <input className="w-full p-2 border border-slate-200 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-900 border-none outline-none focus:ring-1 focus:ring-emerald-500 text-sm text-slate-900 dark:text-white" value={lang || ''} onChange={(e) => updateArrayEntryRoot('languages', idx, e.target.value)} />
               </div>
               <button onClick={() => removeArrayEntryRoot('languages', idx)} className="p-2 mt-1 text-slate-400 hover:text-red-500" title="Remove Language">
                 <Trash2 className="w-4 h-4" />
               </button>
             </div>
           ))}
         </div>
       </section>

       {/* Honors & Awards */}
       <section className="space-y-4">
         <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2">
           <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Honors & Awards</h2>
           <button onClick={() => addArrayEntryRoot('honors')} className="text-sm flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer">
             <Plus className="w-4 h-4" /> Add Honor/Award
           </button>
         </div>
         <div className="space-y-2">
           {data.honors?.map((honor, idx) => (
             <div key={idx} className="flex items-start gap-2">
               <span className="mt-3 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
               <div className="flex-1 min-w-0">
                 <RichInput 
                   value={honor || ''} 
                   onChange={(v) => updateArrayEntryRoot('honors', idx, v)} 
                   minHeight="60px"
                 />
               </div>
               <button onClick={() => removeArrayEntryRoot('honors', idx)} className="p-2 mt-1 text-slate-400 hover:text-red-500" title="Remove Honor/Award">
                 <Trash2 className="w-4 h-4" />
               </button>
             </div>
           ))}
         </div>
       </section>

    </div>
  );
}
